import { Address } from '../user/user.model';

export type ResidenceVerificationStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export type ResidenceSubType = 'RESIDENT' | 'NONRESIDENT';

export type ResidenceDisplayState = 'pending' | 'rejected' | 'active' | 'expiring_soon' | 'expired' | 'renewal_pending';

export interface ResidenceVerification {
    status: ResidenceVerificationStatus;
    proofDocuments: string[];
    reviewedBy: string | null;
    reviewedAt: string | null;
    notes: string | null;
}

export interface ResidenceRenewal {
    status: ResidenceVerificationStatus;
    proofDocuments: string[];
    requestedAt: string;
    reviewedBy: string | null;
    reviewedAt: string | null;
    notes: string | null;
}

export interface Residence {
    _id: string;
    userId: string;
    organisation: string;
    isPrimary: boolean;
    name: string;
    address: Address;
    verification: ResidenceVerification;
    renewal: ResidenceRenewal | null;
    subType: ResidenceSubType | null;
    expirationDate: string | null;
    deleted: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface ReviewResidenceRequest {
    status: 'APPROVED' | 'REJECTED';
    subType?: ResidenceSubType;
    notes?: string;
}

export interface AdminCreateResidenceRequest {
    userId: string;
    address: Address;
    organisation: string;
    subType: ResidenceSubType;
    proofDocuments?: string[];
}

export interface OrgLink {
    tag: string;
    name: string;
    isPrimary: boolean;
    verificationStatus: ResidenceVerificationStatus;
    residenceId: string;
    displayState: ResidenceDisplayState;
    needsReview: boolean;
    expirationDate: string | null;
    subType: ResidenceSubType | null;
}

export interface ProfileAddress {
    address: Address;
    displayName: string;
    orgLinks: OrgLink[];
    isCasual: boolean;
    isVerified: boolean;
}

// Helper functions

export function needsReview(residence: Residence): boolean {
    return residence.verification.status === 'PENDING' || residence.renewal?.status === 'PENDING';
}

export function isRenewalReview(residence: Residence): boolean {
    return residence.renewal?.status === 'PENDING';
}

export function isNewClaimReview(residence: Residence): boolean {
    // A rejected residence can be reconsidered and re-approved; treat it like a new claim
    // so the review dialog shows the sub-type selector and attaches subType on approval.
    return residence.verification.status === 'PENDING' || residence.verification.status === 'REJECTED';
}

export function getReviewableProofDocuments(residence: Residence): string[] {
    if (residence.renewal?.status === 'PENDING') {
        return residence.renewal.proofDocuments;
    }
    return residence.verification.proofDocuments;
}

export function getResidenceDisplayState(residence: Residence): ResidenceDisplayState {
    if (residence.verification.status === 'PENDING') return 'pending';
    if (residence.verification.status === 'REJECTED') return 'rejected';
    if (residence.renewal?.status === 'PENDING') return 'renewal_pending';
    if (!residence.expirationDate) return 'active';

    const now = new Date();
    const expiry = new Date(residence.expirationDate);
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring_soon';
    return 'active';
}

export function getSubTypeLabel(subType: ResidenceSubType | null): string | null {
    if (subType === 'RESIDENT') return 'Resident';
    if (subType === 'NONRESIDENT') return 'Non-Resident';
    return null;
}
